import React from 'react';
import {
    GK_SITUATION_KEYS,
    GK_SITUATION_LABELS,
    GkSituationKey,
    isAllSituations,
} from './insightsGoalkeeperZones';
import {
    collectBundleFilterOptions,
    filterMatchBundles,
    MatchBundleFilterSelection,
} from './insightsMatchBundleFilters';
import { MapPeriodKey } from './insightsMatchStats';
import {
    collectShooterZoneStats,
    mapLocationsByPlayer,
    mapTargetsByPlayer,
    shooterCellsToZoneCounts,
    shootPctText,
} from './insightsPlayerShotZones';
import { MatchBundle } from './insightsQueries';
import {
    ShotFakeKey,
    ShotFoulKey,
    ShotKindFilter,
    shotKindFilterLabel,
    ShotTechniqueKey,
} from './insightsShotKindFilters';
import InsightsSituationMapsShell from './InsightsSituationMapsShell';

interface Props {
    bundles: MatchBundle[];
    ourTeamIds: number[];
    playerId: number;
    /** Parent already applied competition/date/match - hide duplicate scope filters. */
    hideScopeFilters?: boolean;
}

/**
 * Field-player shot maps: one goalmouth + one half-pool,
 * filtered by situation chips (Extra player / 6 to 6 / Penalties / All).
 * Shooter colours (goal green / non-goal red).
 */
const InsightsPlayerShotMaps: React.FC<Props> = ({
    bundles,
    ourTeamIds,
    playerId,
    hideScopeFilters,
}) => {
    const [selected, setSelected] = React.useState<GkSituationKey[]>([...GK_SITUATION_KEYS]);
    const [periodSelected, setPeriodSelected] = React.useState<MapPeriodKey[] | null>(null);
    const [techniqueSelected, setTechniqueSelected] = React.useState<ShotTechniqueKey[] | null>(
        null
    );
    const [foulSelected, setFoulSelected] = React.useState<ShotFoulKey[] | null>(null);
    const [fakeSelected, setFakeSelected] = React.useState<ShotFakeKey[] | null>(null);
    const [showZoneStats, setShowZoneStats] = React.useState(true);
    const [showShotMarks, setShowShotMarks] = React.useState(true);
    // null = all in that dimension; [] = none (Clear).
    const [bundleFilters, setBundleFilters] = React.useState<MatchBundleFilterSelection>({
        competitionIds: null,
        dateFrom: null,
        dateTo: null,
        matchIds: null,
    });

    const bundleFilterOptions = React.useMemo(
        () => collectBundleFilterOptions(bundles, ourTeamIds),
        [bundles, ourTeamIds]
    );

    const sampleBundles = React.useMemo(
        () => filterMatchBundles(bundles, bundleFilters),
        [bundles, bundleFilters]
    );

    const overallStats = React.useMemo(
        () => collectShooterZoneStats(bundles, ourTeamIds, playerId),
        [bundles, ourTeamIds, playerId]
    );

    const sampleOverall = React.useMemo(
        () => collectShooterZoneStats(sampleBundles, ourTeamIds, playerId),
        [sampleBundles, ourTeamIds, playerId]
    );

    const kind: ShotKindFilter = React.useMemo(
        () => ({
            techniques: techniqueSelected,
            fouls: foulSelected,
            fakes: fakeSelected,
        }),
        [techniqueSelected, foulSelected, fakeSelected]
    );

    const filtered = React.useMemo(() => {
        const stats = collectShooterZoneStats(
            sampleBundles,
            ourTeamIds,
            playerId,
            selected,
            periodSelected,
            kind
        );
        return {
            stats,
            goalMap: mapTargetsByPlayer(
                sampleBundles,
                ourTeamIds,
                playerId,
                selected,
                periodSelected,
                kind
            ),
            poolMap: mapLocationsByPlayer(
                sampleBundles,
                ourTeamIds,
                playerId,
                selected,
                periodSelected,
                kind
            ),
        };
    }, [sampleBundles, ourTeamIds, playerId, selected, periodSelected, kind]);

    if (overallStats.shots === 0) return null;

    const situationLabel = isAllSituations(selected)
        ? GK_SITUATION_LABELS.all
        : selected.map((key) => GK_SITUATION_LABELS[key]).join(' + ');
    const kindLabel = shotKindFilterLabel(kind);
    const filterLabel = [situationLabel, kindLabel].filter(Boolean).join(' · ');

    return (
        <InsightsSituationMapsShell
            title="Shooting"
            hint="Shooting % = goals / shots · green = goal · red = miss / saved / blocked"
            colorMode="shooter"
            selected={selected}
            onSelectedChange={setSelected}
            periodSelected={periodSelected}
            onPeriodSelectedChange={setPeriodSelected}
            showZoneStats={showZoneStats}
            onShowZoneStatsChange={setShowZoneStats}
            showShotMarks={showShotMarks}
            onShowShotMarksChange={setShowShotMarks}
            bundleFilterOptions={bundleFilterOptions}
            bundleFilters={bundleFilters}
            onBundleFiltersChange={setBundleFilters}
            hideScopeFilters={hideScopeFilters}
            techniqueSelected={techniqueSelected}
            onTechniqueSelectedChange={setTechniqueSelected}
            foulSelected={foulSelected}
            onFoulSelectedChange={setFoulSelected}
            fakeSelected={fakeSelected}
            onFakeSelectedChange={setFakeSelected}
            statsStrip={
                <div className="insightsStatStrip">
                    <div className="insightsStat">
                        <b>{shootPctText(sampleOverall.goals, sampleOverall.shots)}</b>
                        <i>Shooting %</i>
                    </div>
                    <div className="insightsStat">
                        <b>{sampleOverall.shots}</b>
                        <i>Shots</i>
                    </div>
                    <div className="insightsStat">
                        <b>{sampleOverall.goals}</b>
                        <i>Goals</i>
                    </div>
                    <div className="insightsStat">
                        <b>{sampleOverall.misses}</b>
                        <i>Misses</i>
                    </div>
                </div>
            }
            sampleNote={
                sampleOverall.sampleWarning ? (
                    <p className="insightsTableNote">
                        Small sample ({sampleOverall.shots} shots) - treat % carefully.
                    </p>
                ) : null
            }
            filterSummary={
                filtered.stats.shots > 0 ? (
                    <span>
                        {filterLabel}: {shootPctText(filtered.stats.goals, filtered.stats.shots)} ·{' '}
                        {filtered.stats.goals}G · {filtered.stats.misses}M · {filtered.stats.shots}{' '}
                        Sh
                    </span>
                ) : (
                    <span>{filterLabel}: no shots in this sample</span>
                )
            }
            goalMap={filtered.goalMap}
            poolMap={filtered.poolMap}
            goalZoneCounts={shooterCellsToZoneCounts(filtered.stats.cells)}
            poolZoneCounts={shooterCellsToZoneCounts(filtered.stats.poolCells)}
            goalEmptyHint="No goalmouth targets on this player's shots."
            poolEmptyHint="No pool locations on this player's shots."
        />
    );
};

export default InsightsPlayerShotMaps;
